import React, { useContext, useEffect, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { Place } from '@mui/icons-material'
import API from '../../services/API'
import { GlobalContext } from '../../App'

const DEFAULT_CENTER = [-1.5, 52.5]
const MAP_STYLE = 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json'

const getCoords = (base) => {
  const coords = base?.location?.coordinates
  return Array.isArray(coords) &&
    coords.length === 2 &&
    coords.every((value) => Number.isFinite(Number(value)))
    ? coords.map(Number)
    : null
}

const buildAddressLine = (address = {}) =>
  [address.lineOne, address.town, address.postCode].filter(Boolean).join(', ')

const markerElement = (name, selected) => {
  const el = document.createElement('div')
  el.style.position = 'relative'
  el.style.width = selected ? '26px' : '22px'
  el.style.height = selected ? '26px' : '22px'
  el.style.borderRadius = '999px'
  el.style.background = selected ? '#E32525' : '#239AA4'
  el.style.border = '3px solid white'
  el.style.boxShadow = '0 6px 16px rgba(0,0,0,0.35)'
  el.style.cursor = 'pointer'

  const label = document.createElement('div')
  label.textContent = name || 'Base'
  label.style.position = 'absolute'
  label.style.top = selected ? '30px' : '26px'
  label.style.left = '50%'
  label.style.transform = 'translateX(-50%)'
  label.style.whiteSpace = 'nowrap'
  label.style.fontSize = '11px'
  label.style.fontWeight = '700'
  label.style.padding = '1px 6px'
  label.style.borderRadius = '6px'
  label.style.background = 'rgba(15,23,42,0.85)'
  label.style.color = 'white'
  label.style.pointerEvents = 'none'
  el.appendChild(label)

  return el
}

// One map of every base location. Site admins click a pin (or "place on
// map" for a base without one) to drag/click its map point into position —
// the point drives base-to-base mileage and routing, and moving it never
// touches the base's postal address.
const BaseLocationsMap = ({ locations = [], canEdit, onSaved }) => {
  const { setToast } = useContext(GlobalContext)
  const mapContainer = useRef(null)
  const mapRef = useRef(null)
  const markersRef = useRef([])
  const selectedIdRef = useRef(null)
  const [selectedId, setSelectedId] = useState(null)
  const [pendingCoords, setPendingCoords] = useState(null)
  const [saving, setSaving] = useState(false)

  const selectedBase = locations.find(
    (base) => String(base._id) === String(selectedId),
  )
  const storedCoords = selectedBase ? getCoords(selectedBase) : null
  const dirty =
    !!pendingCoords &&
    JSON.stringify(pendingCoords) !== JSON.stringify(storedCoords)
  const unmappedBases = locations.filter((base) => !getCoords(base))

  useEffect(() => {
    selectedIdRef.current = selectedId
    const canvas = mapRef.current?.getCanvas()
    if (canvas) canvas.style.cursor = selectedId ? 'crosshair' : ''
  }, [selectedId])

  useEffect(() => {
    if (mapRef.current) return undefined

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: MAP_STYLE,
      center: DEFAULT_CENTER,
      zoom: 5.5,
      attributionControl: true,
    })
    map.addControl(new maplibregl.NavigationControl(), 'bottom-left')
    map.on('click', (event) => {
      if (!selectedIdRef.current) return
      setPendingCoords([
        Number(event.lngLat.lng.toFixed(6)),
        Number(event.lngLat.lat.toFixed(6)),
      ])
    })
    mapRef.current = map

    return () => {
      markersRef.current.forEach((marker) => marker.remove())
      markersRef.current = []
      map.remove()
      mapRef.current = null
    }
  }, [])

  // Fit to the mapped bases — but never yank the view while a point is
  // being edited
  useEffect(() => {
    const map = mapRef.current
    if (!map || selectedId) return
    const points = locations.map(getCoords).filter(Boolean)
    if (!points.length) return
    if (points.length === 1) {
      map.jumpTo({ center: points[0], zoom: 12 })
      return
    }
    const bounds = points.reduce(
      (acc, point) => acc.extend(point),
      new maplibregl.LngLatBounds(points[0], points[0]),
    )
    map.fitBounds(bounds, { padding: 80, maxZoom: 12, duration: 0 })
  }, [locations, selectedId])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    markersRef.current.forEach((marker) => marker.remove())
    markersRef.current = []

    locations.forEach((base) => {
      const isSelected = String(base._id) === String(selectedId)
      const coords =
        isSelected && pendingCoords ? pendingCoords : getCoords(base)
      if (!coords) return

      const el = markerElement(base.name, isSelected)
      el.addEventListener('click', (event) => {
        event.stopPropagation()
        if (!canEdit || selectedIdRef.current === String(base._id)) return
        setSelectedId(String(base._id))
        setPendingCoords(getCoords(base))
      })

      const marker = new maplibregl.Marker({
        element: el,
        draggable: isSelected && canEdit,
      })
        .setLngLat(coords)
        .addTo(map)

      if (isSelected && canEdit) {
        marker.on('dragend', () => {
          const position = marker.getLngLat()
          setPendingCoords([
            Number(position.lng.toFixed(6)),
            Number(position.lat.toFixed(6)),
          ])
        })
      }
      markersRef.current.push(marker)
    })
  }, [locations, selectedId, pendingCoords, canEdit])

  const closeEditor = () => {
    setSelectedId(null)
    setPendingCoords(null)
  }

  const saveMapPoint = async () => {
    if (!selectedId || !pendingCoords) return
    setSaving(true)
    try {
      const res = await API.post('/edit-base-location', {
        _id: selectedId,
        location: { type: 'Point', coordinates: pendingCoords },
      })
      if (res.status === 200) {
        setToast({ message: 'Map point updated' })
        closeEditor()
        onSaved?.()
      } else {
        setToast({
          message: 'Unable to update the map point. Please try again.',
          severity: 'error',
        })
      }
    } catch (e) {
      setToast({
        message: 'Unable to update the map point. Please try again.',
        severity: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Stack gap={1}>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="body2" fontWeight={700}>
          Map points
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {canEdit
            ? 'Click a pin to fine-tune where a base sits on the map.'
            : 'Where each base sits on the map.'}
        </Typography>
        {canEdit &&
          unmappedBases.map((base) => (
            <Chip
              key={base._id}
              size="small"
              color="warning"
              variant="outlined"
              icon={<Place />}
              label={`${base.name}: no map point — place on map`}
              onClick={() => {
                setSelectedId(String(base._id))
                setPendingCoords(null)
              }}
            />
          ))}
      </Stack>
      <Box
        sx={{
          position: 'relative',
          height: 380,
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box ref={mapContainer} sx={{ width: '100%', height: '100%' }} />

        {selectedBase && (
          <Paper
            elevation={4}
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              p: 1.5,
              borderRadius: 2,
              maxWidth: 340,
            }}
          >
            <Stack gap={0.75}>
              <Typography variant="body2" fontWeight={700}>
                {selectedBase.name}
              </Typography>
              {buildAddressLine(selectedBase.address) && (
                <Typography variant="caption" color="text.secondary">
                  {buildAddressLine(selectedBase.address)}
                </Typography>
              )}
              <Typography variant="caption">
                {pendingCoords
                  ? 'Drag the pin or click the map to move this base’s map point.'
                  : 'Click the map where this base is to place its map point.'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This only moves the map point used for mileage and routing —
                the postal address is not changed. If the address is wrong,
                edit the base from the list instead.
              </Typography>
              {pendingCoords && (
                <Typography variant="caption" color="text.secondary">
                  {pendingCoords[1].toFixed(5)}, {pendingCoords[0].toFixed(5)}
                </Typography>
              )}
              <Stack direction="row" gap={1}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={saveMapPoint}
                  disabled={!dirty || saving}
                  startIcon={saving ? <CircularProgress size={14} /> : null}
                >
                  {saving ? 'Saving...' : 'Save map point'}
                </Button>
                {dirty && storedCoords && (
                  <Button
                    size="small"
                    onClick={() => setPendingCoords(storedCoords)}
                    disabled={saving}
                  >
                    Reset
                  </Button>
                )}
                <Button size="small" onClick={closeEditor} disabled={saving}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}
      </Box>
    </Stack>
  )
}

export default BaseLocationsMap
